import { motion } from "framer-motion";
import { useMemo } from "react";
import "./TrendingTicker.css";

type RawToken = {
  id?: string;
  name?: string;
  symbol?: string;
  image?: string;
};

type TrendingTickerProps = {
  tokens?: RawToken[];
  onTokenClick?: (tokenId: string) => void;
};

type DisplayToken = {
  key: string;
  rank: string;
  name: string;
  badge: string;
  gradient: string;
  change: string;
  tone: "up" | "down" | "flat";
  id?: string;
  logo?: string;
  duplicate?: boolean;
};

const fallbackTokens: DisplayToken[] = [
  {
    key: "fallback-suyan",
    rank: "#1",
    name: "SUYAN",
    badge: "🔥",
    gradient: "linear-gradient(135deg, #34d399 0%, #059669 100%)",
    change: "+8.66%",
    tone: "up",
  },
  {
    key: "fallback-lemon",
    rank: "#2",
    name: "LEMON",
    badge: "🍋",
    gradient: "linear-gradient(135deg, #fde68a 0%, #f59e0b 100%)",
    change: "+0.27%",
    tone: "up",
  },
  {
    key: "fallback-bits",
    rank: "#3",
    name: "BITS",
    badge: "🪙",
    gradient: "linear-gradient(135deg, #93c5fd 0%, #3b82f6 100%)",
    change: "+0.21%",
    tone: "up",
  },
  {
    key: "fallback-goon",
    rank: "#4",
    name: "GOON",
    badge: "🐒",
    gradient: "linear-gradient(135deg, #fca5a5 0%, #ef4444 100%)",
    change: "+0.16%",
    tone: "up",
  },
  {
    key: "fallback-jai",
    rank: "#5",
    name: "JAI",
    badge: "🧟",
    gradient: "linear-gradient(135deg, #ddd6fe 0%, #8b5cf6 100%)",
    change: "+0.11%",
    tone: "up",
  },
  {
    key: "fallback-test",
    rank: "#6",
    name: "TEST",
    badge: "🧠",
    gradient: "linear-gradient(135deg, #fbcfe8 0%, #ec4899 100%)",
    change: "+0.09%",
    tone: "up",
  },
  {
    key: "fallback-peii",
    rank: "#7",
    name: "PEII",
    badge: "🐚",
    gradient: "linear-gradient(135deg, #a5f3fc 0%, #0ea5e9 100%)",
    change: "+0.04%",
    tone: "up",
  },
  {
    key: "fallback-shork",
    rank: "#8",
    name: "SHORK",
    badge: "🦈",
    gradient: "linear-gradient(135deg, #bae6fd 0%, #0284c7 100%)",
    change: "+0.01%",
    tone: "up",
  },
];

const changePalette = [
  "+8.66%",
  "+4.12%",
  "+2.54%",
  "+1.27%",
  "+0.64%",
  "+0.31%",
  "+0.08%",
  "0.00%",
  "-0.12%",
];

const gradientPalette = [
  "linear-gradient(135deg, #34d399 0%, #059669 100%)",
  "linear-gradient(135deg, #facc15 0%, #f97316 100%)",
  "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)",
  "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)",
  "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
  "linear-gradient(135deg, #2dd4bf 0%, #0f766e 100%)",
  "linear-gradient(135deg, #fb923c 0%, #ea580c 100%)",
  "linear-gradient(135deg, #c084fc 0%, #7c3aed 100%)",
];

const TrendingTicker: React.FC<TrendingTickerProps> = ({ tokens = [], onTokenClick }) => {
  const displayTokens = useMemo<DisplayToken[]>(() => {
    if (!tokens || tokens.length === 0) {
      return fallbackTokens;
    }

    const sorted = [...tokens].sort((a, b) => {
      const nameA = (a.name || a.symbol || "").toUpperCase();
      const nameB = (b.name || b.symbol || "").toUpperCase();
      return nameA.localeCompare(nameB);
    });

    const enriched: RawToken[] = sorted.slice(0, 12);
    while (enriched.length < 8) {
      const fallback = fallbackTokens[enriched.length % fallbackTokens.length];
      enriched.push({
        id: fallback.key,
        name: fallback.name,
        symbol: fallback.badge,
      });
    }

    return enriched.map((token, index) => {
      const baseName = token.name?.trim() || token.symbol?.trim() || `Token ${index + 1}`;
      const badge = token.symbol?.trim().slice(0, 3) || baseName.slice(0, 3);
      const change = changePalette[index % changePalette.length];
      const tone = change.startsWith("-") ? "down" : change === "0.00%" ? "flat" : "up";

      return {
        key: token.id ?? `${badge}-${index}`,
        rank: `#${index + 1}`,
        name: baseName,
        badge,
        gradient: gradientPalette[index % gradientPalette.length],
        change,
        tone,
        id: token.id ? String(token.id) : undefined,
        logo: token.image,
      };
    });
  }, [tokens]);

  const marqueeTokens = useMemo(() => {
    const duplicated = displayTokens.map((token) => ({ ...token, duplicate: true }));
    return [...displayTokens, ...duplicated];
  }, [displayTokens]);

  return (
    <div className="trending-ticker-container">
      <div className="trending-ticker-glow" />
      <motion.div
        className="trending-ticker-track"
        animate={{ x: ["0%", "-100%"] }}
        transition={{ repeat: Infinity, duration: 50, ease: "linear" }}
      >
        {marqueeTokens.map((token, index) => {
          const isInteractive = Boolean(token.id && onTokenClick);

          const handleActivate = () => {
            if (isInteractive && token.id) {
              onTokenClick?.(token.id);
            }
          };

          return (
            <div
              className={`trending-token-pill${token.duplicate ? " duplicate" : ""}${isInteractive ? " clickable" : ""}`}
              key={`${token.key}-${index}`}
              role={isInteractive ? "button" : undefined}
              tabIndex={isInteractive ? 0 : -1}
              onClick={isInteractive ? handleActivate : undefined}
              onKeyDown={
                isInteractive
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleActivate();
                      }
                    }
                  : undefined
              }
            >
              <span className="token-rank">{token.rank}</span>
              {token.logo ? (
                <span className="token-logo-wrap">
                  <img src={token.logo} alt={token.name} loading="lazy" />
                </span>
              ) : (
                <span
                  className="token-badge"
                  style={{ background: token.gradient }}
                  aria-hidden="true"
                >
                  {token.badge}
                </span>
              )}
              <span className="token-name">{token.name}</span>
              <span className={`token-change token-change-${token.tone}`}>
                {token.change}
              </span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default TrendingTicker;
